// Character appearance: the data shape, the option catalogs, and the
// sanitizer. This is protocol — the server validates and persists it, every
// renderer draws from it. Values are indices into the catalogs plus a few
// 0..1 sliders, so bad input can always be clamped into something valid.
//
// v2 (avatar identity): garb, wear, marks, accessories, and the "lives"
// presets. Compatibility rule: every new catalog APPENDS (indices are
// stored in rows and replicated), and every new field's sanitized default
// (index 0 / slider 0.5) renders exactly like a v1 avatar did — an old row
// must come out of parseAppearance looking like it always has.

export interface Appearance {
  /** Body build index into BUILDS. */
  build: number;
  /** 0..1 → height scale HEIGHT_MIN..HEIGHT_MAX. */
  height: number;
  /** Index into SKIN_TONES. */
  skinTone: number;
  /** Face preset index into FACE_PRESETS. */
  face: number;
  /** 0..1 tweak sliders around the preset. */
  eyeSize: number;
  eyeSpacing: number;
  /** 0..1: brow weight, thin to heavy (0.5 = the v1 brow). */
  brow: number;
  /** 0..1: head/jaw width, narrow to square (0.5 = the v1 head). */
  jaw: number;
  /** Index into HAIR_STYLES / HAIR_COLORS. */
  hair: number;
  hairColor: number;
  /** Index into OUTFITS / OUTFIT_COLORS. */
  outfit: number;
  outfitColor: number;
  /** Index into WEAR_LEVELS (0 = fresh-pressed, the v1 look). */
  wear: number;
  /** Index into FACE_MARKS (0 = unmarked). */
  marks: number;
  /** Index into ACCESSORIES (0 = none). */
  accessory: number;
}

/** Body silhouettes: shoulder/hip width scales relative to average. */
export const BUILDS = [
  { id: "slight", label: "Slight", width: 0.86 },
  { id: "average", label: "Average", width: 1.0 },
  { id: "broad", label: "Broad", width: 1.18 },
  // Appended v2. Kept inside what the rig and the doorways tolerate: the
  // capsule collision radius (0.35 m) is fixed, so silhouette width is
  // visual only and must not grow past arm's reach of it.
  { id: "stocky", label: "Stocky", width: 1.3 },
] as const;

export const HEIGHT_MIN = 0.92;
export const HEIGHT_MAX = 1.1;

/** Stylized skin palette, warm to cool. Appended, never reordered. */
export const SKIN_TONES = [
  0xf5d7b8, 0xeec39a, 0xd9a066, 0xc68642, 0xa0642d, 0x8d5524, 0x6e3f1d, 0x4a2c14,
  // v2: olive and deep umber, so the palette doesn't skip anyone.
  0xb08d5a, 0x3a2314,
] as const;

/** Face presets: eye/mouth style drawn on the face texture. */
export const FACE_PRESETS = [
  { id: "steady", label: "Steady" },
  { id: "bright", label: "Bright" },
  { id: "heavy-lidded", label: "Heavy-lidded" },
  { id: "sharp", label: "Sharp" },
  { id: "soft", label: "Soft" },
] as const;

export const HAIR_STYLES = [
  { id: "buzz", label: "Buzz" },
  { id: "crop", label: "Crop" },
  { id: "quiff", label: "Quiff" },
  { id: "ponytail", label: "Ponytail" },
  { id: "buns", label: "Buns" },
  { id: "cloud", label: "Cloud" },
  // v2 append.
  { id: "swept", label: "Swept back" },
] as const;

export const HAIR_COLORS = [
  0x1b1b22, 0x3a2a1c, 0x6e4a2c, 0xa87844, 0xc9a55a, 0x8f9aa8, 0xc4453f, 0x3f6f8f,
  // v2: aged white and verdigris — the settlement has elders and eccentrics.
  0xe0d8c8, 0x5a7a52,
] as const;

export const OUTFITS = [
  { id: "spacer", label: "Spacer jacket" },
  { id: "duster", label: "Duster coat" },
  { id: "deckhand", label: "Deckhand vest" },
  // v2 append — blue-collar garb, per the roadmap's H1 identity work.
  { id: "machinist", label: "Machinist's apron" },
  { id: "freighter", label: "Freighter coat" },
  { id: "boiler", label: "Boiler suit" },
] as const;

/** Accent palettes; the base garment stays muted, the accent carries it. */
export const OUTFIT_COLORS = [
  0x8f2d3c, 0x2d6a8f, 0x8a6d2f, 0x3c7a4e, 0x5b4a8a, 0x374049,
  // v2: brass and rust — the street's own colours.
  0x9c7a30, 0x7a4a2a,
] as const;

/**
 * Wear-and-tear on the garment: the lived-in law as a dial. Index 0 is
 * exactly the v1 material response. `tint` multiplies garment colour,
 * `gray` lerps it toward washed-out, `rough` adds roughness, `patches`
 * puts mending on knees and elbows.
 */
export const WEAR_LEVELS = [
  { id: "fresh", label: "Fresh-pressed", tint: 1.0, gray: 0, rough: 0, patches: false },
  { id: "worked", label: "Worked-in", tint: 0.92, gray: 0.06, rough: 0.05, patches: false },
  { id: "roadworn", label: "Road-worn", tint: 0.82, gray: 0.14, rough: 0.12, patches: true },
  { id: "scorched", label: "Aether-scorched", tint: 0.7, gray: 0.24, rough: 0.18, patches: true },
] as const;

/** Marks of a life lived, drawn on the face. Index 0 = unmarked (v1). */
export const FACE_MARKS = [
  { id: "none", label: "Unmarked" },
  { id: "brow-scar", label: "Scarred brow" },
  { id: "cheek-scar", label: "Cheek scar" },
  { id: "grease", label: "Grease smudge" },
  { id: "freckles", label: "Freckles" },
  { id: "aether-burn", label: "Aether burn" },
] as const;

/** Free accessories (roadmap H1: hats, goggles — no store, ever). */
export const ACCESSORIES = [
  { id: "none", label: "Bare-headed" },
  { id: "flat-cap", label: "Flat cap" },
  { id: "bowler", label: "Bowler" },
  { id: "goggles", label: "Goggles, worn" },
  { id: "goggles-up", label: "Goggles, raised" },
  { id: "scarf", label: "Dock scarf" },
] as const;

export const CHARACTER_NAME_MAX = 24;

function clamp01(v: unknown): number {
  const n = Number(v);
  if (!Number.isFinite(n)) return 0.5;
  return n < 0 ? 0 : n > 1 ? 1 : n;
}

function index(v: unknown, length: number): number {
  const n = Math.trunc(Number(v));
  if (!Number.isFinite(n) || n < 0 || n >= length) return 0;
  return n;
}

/** Coerce anything into a valid Appearance (server-side gatekeeper). */
export function sanitizeAppearance(raw: unknown): Appearance {
  const a = (raw ?? {}) as Record<string, unknown>;
  return {
    build: index(a.build, BUILDS.length),
    height: clamp01(a.height),
    skinTone: index(a.skinTone, SKIN_TONES.length),
    face: index(a.face, FACE_PRESETS.length),
    eyeSize: clamp01(a.eyeSize),
    eyeSpacing: clamp01(a.eyeSpacing),
    brow: clamp01(a.brow),
    jaw: clamp01(a.jaw),
    hair: index(a.hair, HAIR_STYLES.length),
    hairColor: index(a.hairColor, HAIR_COLORS.length),
    outfit: index(a.outfit, OUTFITS.length),
    outfitColor: index(a.outfitColor, OUTFIT_COLORS.length),
    wear: index(a.wear, WEAR_LEVELS.length),
    marks: index(a.marks, FACE_MARKS.length),
    accessory: index(a.accessory, ACCESSORIES.length),
  };
}

/** Parse a replicated appearance JSON string; never throws. */
export function parseAppearance(json: string): Appearance {
  try {
    return sanitizeAppearance(JSON.parse(json));
  } catch {
    return sanitizeAppearance({});
  }
}

/**
 * Trim/validate a character display name. Returns undefined when nothing
 * usable remains (caller decides the error path).
 */
export function sanitizeCharacterName(raw: unknown): string | undefined {
  if (typeof raw !== "string") return undefined;
  const cleaned = raw
    .replace(/[\p{Cc}\p{Cf}]/gu, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, CHARACTER_NAME_MAX);
  return cleaned.length >= 2 ? cleaned : undefined;
}

// ---------------------------------------------------------------------------
// Lives: curated starting looks in the world's voice. NOT classes — a life
// here is a look and nothing else. Applying one never touches skin tone,
// hair colour, or the face sliders: whose face it is stays yours.

export interface Life {
  id: string;
  label: string;
  /** One line of flavor for the card, in the room's voice. */
  line: string;
  look: Pick<
    Appearance,
    "build" | "height" | "hair" | "outfit" | "outfitColor" | "wear" | "marks" | "accessory"
  >;
}

export const LIVES = [
  {
    id: "dock-hand",
    label: "Dock hand",
    line: "Rope-burned palms and boots that have carried worse.",
    look: {
      build: 2,
      height: 0.6,
      hair: 1,
      outfit: 2,
      outfitColor: 5,
      wear: 2,
      marks: 3,
      accessory: 1,
    },
  },
  {
    id: "archivist",
    label: "Archivist",
    line: "Keeps the ledgers nobody else will read. Ink under one nail.",
    look: {
      build: 0,
      height: 0.45,
      hair: 6,
      outfit: 0,
      outfitColor: 4,
      wear: 0,
      marks: 0,
      accessory: 0,
    },
  },
  {
    id: "freight-pilot",
    label: "Freight pilot",
    line: "Flies ugly routes on honest time. Sleeps in the coat.",
    look: {
      build: 1,
      height: 0.55,
      hair: 2,
      outfit: 4,
      outfitColor: 1,
      wear: 2,
      marks: 0,
      accessory: 4,
    },
  },
  {
    id: "boiler-tech",
    label: "Boiler tech",
    line: "Knows every pressure line by its whine. Smells of hot brass.",
    look: {
      build: 3,
      height: 0.5,
      hair: 0,
      outfit: 5,
      outfitColor: 7,
      wear: 3,
      marks: 5,
      accessory: 3,
    },
  },
  {
    id: "cantina-regular",
    label: "Cantina regular",
    line: "Every room has one. This one is yours now.",
    look: {
      build: 1,
      height: 0.5,
      hair: 5,
      outfit: 1,
      outfitColor: 0,
      wear: 1,
      marks: 2,
      accessory: 0,
    },
  },
  {
    id: "line-cook",
    label: "Line cook",
    line: "Feeds the night shift. Never once written a menu down.",
    look: {
      build: 2,
      height: 0.4,
      hair: 4,
      outfit: 3,
      outfitColor: 2,
      wear: 1,
      marks: 3,
      accessory: 5,
    },
  },
  {
    id: "skiff-courier",
    label: "Skiff courier",
    line: "Anywhere on the planet by morning, weather permitting. It never permits.",
    look: {
      build: 0,
      height: 0.55,
      hair: 3,
      outfit: 2,
      outfitColor: 3,
      wear: 2,
      marks: 4,
      accessory: 4,
    },
  },
  {
    id: "deck-musician",
    label: "Deck musician",
    line: "Plays the late set for whoever is still upright.",
    look: {
      build: 1,
      height: 0.6,
      hair: 2,
      outfit: 0,
      outfitColor: 6,
      wear: 1,
      marks: 0,
      accessory: 0,
    },
  },
  {
    id: "prospector",
    label: "Prospector",
    line: "Came out for the copper. Stayed for the quiet.",
    look: {
      build: 2,
      height: 0.65,
      hair: 1,
      outfit: 1,
      outfitColor: 7,
      wear: 3,
      marks: 1,
      accessory: 2,
    },
  },
  {
    id: "union-steward",
    label: "Union steward",
    line: "Signs last, reads everything, buys the first round.",
    look: {
      build: 3,
      height: 0.55,
      hair: 1,
      outfit: 4,
      outfitColor: 5,
      wear: 1,
      marks: 0,
      accessory: 2,
    },
  },
] as const satisfies readonly Life[];

/** A neutral, sanitized starting point for the creator. */
export function defaultAppearance(): Appearance {
  return sanitizeAppearance({
    build: 1,
    height: 0.5,
    skinTone: 2,
    face: 0,
    eyeSize: 0.5,
    eyeSpacing: 0.5,
    brow: 0.5,
    jaw: 0.5,
    hair: 1,
    hairColor: 1,
    outfit: 0,
    outfitColor: 5,
    wear: 1,
    marks: 0,
    accessory: 0,
  });
}

/**
 * Dress an appearance in a life. Only the look fields change — skin tone,
 * hair colour, and every face parameter survive, because a life is a job's
 * worth of clothes and wear, not a person.
 */
export function applyLife(a: Appearance, life: Life): Appearance {
  return sanitizeAppearance({ ...a, ...life.look });
}

/**
 * A coherent random appearance: a random life, jittered — never a clown.
 * Garb/wear/marks stay inside the life's own story; the person inside it
 * (skin, face, hair colour) is fully rolled.
 */
export function randomAppearance(rng: () => number = Math.random): Appearance {
  const life = LIVES[Math.floor(rng() * LIVES.length)] ?? LIVES[0];
  const jitter = (v: number, by: number): number => v + (rng() * 2 - 1) * by;
  return sanitizeAppearance({
    ...applyLife(defaultAppearance(), life),
    height: jitter(life.look.height, 0.2),
    skinTone: Math.floor(rng() * SKIN_TONES.length),
    face: Math.floor(rng() * FACE_PRESETS.length),
    eyeSize: 0.3 + rng() * 0.4,
    eyeSpacing: 0.3 + rng() * 0.4,
    brow: 0.25 + rng() * 0.5,
    jaw: 0.25 + rng() * 0.5,
    hairColor: Math.floor(rng() * HAIR_COLORS.length),
    // A quarter of the time the accent drifts within the palette — the
    // same coat bought in a different year.
    outfitColor: rng() < 0.25 ? Math.floor(rng() * OUTFIT_COLORS.length) : life.look.outfitColor,
  });
}
